/**
 * The relink commands (M3 plan R19, §2.6.2): `buyParts` (a parts vendor in hail range), `deliverParts` (at the
 * repair port), `beginReboot` (every part delivered), `repairLaneRing` (a down ring in range, with the parts) and
 * `chooseSponsor` (after a relink). Refusals are results, never exceptions, and change nothing. UI-rate events.
 * Under the strategic economy `buyParts` buys from the station's stock through the career port (SY-2e).
 */
import { FACTION_KEYS } from '../galaxy/layout/config'
import type { FactionKey } from '../galaxy/layout/types'
import { emitReach, REV } from '../reach/events'
import type { ReachCore } from '../reach/runtime/types'
import { inHailRange } from '../reach/session/hailRange'
import { addDelivered, laneRingUp, setLaneRingUp } from '../reach/state'
import { PART_IDS, type PartId, type ReachCommand, type ReachCommandResult } from '../reach/types'
import type { Sim } from '../sim'
import { Pos64 } from '../world/components'
import { deliveredOf, dist2, ensureSlots, isScanned, livePlayer, missingTotal, persistable, relayStatus, speedOf, texts, type RelinkContext } from './context'
import { startReboot } from './reboot'
import { PART_LABEL, partsText } from './rules'

type Cmd<K extends ReachCommand['kind']> = Extract<ReachCommand, { kind: K }>

/** Float tolerance so 20 t of free space over 1 t parts never floors to 19 on rounding noise. */
const FIT_EPSILON = 1e-9

function refuse(error: Exclude<ReachCommandResult, { ok: true }>['error'], message: string): ReachCommandResult {
  return { ok: false, error, message }
}

function isPartId(value: string): value is PartId {
  return (PART_IDS as readonly string[]).includes(value)
}

/** "3:30" for 210 seconds. */
export function minutesSeconds(seconds: number): string {
  const s = Math.max(0, Math.round(seconds))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/** A relay slot of the current system for `relayId`, or a refusal. */
function slotFor(ctx: RelinkContext, core: ReachCore, relayId: string): number | ReachCommandResult {
  const slot = ensureSlots(ctx, core).slotOf.get(relayId)
  return slot === undefined ? refuse('invalid', 'That relay is not in this system') : slot
}

/** The living player within `portRangeM` of the slot's repair port (and below `portMaxSpeed` when `slow`), or a refusal. */
function atPort(ctx: RelinkContext, core: ReachCore, sim: Sim, slot: number, slow: boolean): number | ReachCommandResult {
  const p = livePlayer(core, sim)
  const port = ctx.slots.port
  const range = ctx.cfg.portRangeM
  if (p < 0 || dist2(p, port[slot * 3], port[slot * 3 + 1], port[slot * 3 + 2]) > range * range) {
    return refuse('outOfRange', `Fly within ${Math.round(range)} m of the relay's repair port`)
  }
  if (slow && speedOf(p) > ctx.cfg.portMaxSpeed) return refuse('outOfRange', texts(ctx).slow)
  return p
}

/** The refusal for a relay that is not offline, or null. */
function notOffline(core: ReachCore, ctx: RelinkContext, slot: number): ReachCommandResult | null {
  const status = relayStatus(core, ctx.slots.sites[slot])
  if (status === 'online') return refuse('invalid', 'The relay is already online')
  if (status === 'reboot') return refuse('busy', 'The relay is rebooting')
  return null
}

/** What is still missing at the slot, per part. */
function missingAt(core: ReachCore, ctx: RelinkContext, slot: number): Record<PartId, number> {
  const id = ctx.slots.sites[slot].relayId
  const out: Record<PartId, number> = { hull_plating: 0, electronics: 0, drive_parts: 0 }
  for (let p = 0; p < PART_IDS.length; p++) out[PART_IDS[p]] = Math.max(0, ctx.slots.required[slot * 3 + p] - deliveredOf(core, id, PART_IDS[p]))
  return out
}

export function buyParts(ctx: RelinkContext, core: ReachCore, sim: Sim, cmd: Cmd<'buyParts'>): ReachCommandResult {
  const station = core.session.layout?.stations.find((s) => s.id === cmd.stationId)
  if (station === undefined) return refuse('invalid', 'That station is not in this system')
  if (!station.services.includes('parts-vendor')) return refuse('invalid', `${station.name} does not sell relay parts`)
  const p = livePlayer(core, sim)
  const hail = ctx.session.hail.rangeM
  if (p < 0 || !inHailRange(station, Pos64.x[p], Pos64.y[p], Pos64.z[p], hail)) {
    return refuse('outOfRange', `Fly within ${Math.round(hail / 1000)} km of the hull of ${station.name}`)
  }
  if (!isPartId(cmd.part)) return refuse('invalid', 'Unknown part')
  const qty = cmd.qty
  if (!Number.isInteger(qty) || qty < 1) return refuse('invalid', 'Buy at least one part')
  const massT = ctx.standIn.partMassT[cmd.part]
  const fits = Math.floor(core.career.cargoFreeT() / massT + FIT_EPSILON)
  const label = PART_LABEL[cmd.part]
  if (fits < 1) return refuse('cargo', 'Hold full')
  if (qty > fits) return refuse('cargo', `Room for only ${fits} ${label}`)
  // Economy plan SY-2e: whenever the strategic market runs, the part is a transfer out of the station's stock at its
  // curve price (the career's quote → commit path); `partPrices` below prices only the offline stand-in.
  const quote = core.career.goodQuote?.(station.id, cmd.part) ?? null
  if (quote !== null && core.career.buyGood !== undefined) {
    if (quote.unit === null) return refuse('invalid', `${station.name} does not sell ${label}`)
    if (quote.stock < 1) return refuse('invalid', `${station.name} has no ${label} in stock`)
    if (qty > quote.stock) return refuse('invalid', `${station.name} has only ${quote.stock} ${label} in stock`)
    return core.career.buyGood(station.id, cmd.part, qty)
  }
  const price = ctx.standIn.partPrices[cmd.part]
  if (!core.career.spend(qty * price, `relay parts: ${qty} ${label}`)) return refuse('funds', 'Not enough credits')
  const added = core.career.addCargo(cmd.part, qty)
  // A hold that took fewer than it said would fit: refund the rest.
  if (added < qty) core.career.earn((qty - added) * price, `relay parts refund: ${qty - added} ${label}`)
  return { ok: true, message: `Bought ${added} ${label}` }
}

export function deliverParts(ctx: RelinkContext, core: ReachCore, sim: Sim, cmd: Cmd<'deliverParts'>): ReachCommandResult {
  const slot = slotFor(ctx, core, cmd.relayId)
  if (typeof slot !== 'number') return slot
  const state = notOffline(core, ctx, slot)
  if (state !== null) return state
  if (!isScanned(core, cmd.relayId)) return refuse('invalid', `Scan the relay first (fly within ${Math.round(ctx.cfg.autoScanM / 1000)} km)`)
  const p = atPort(ctx, core, sim, slot, true)
  if (typeof p !== 'number') return p
  const missing = missingAt(core, ctx, slot)
  if (missingTotal(core, ctx.slots, slot) === 0) return refuse('invalid', 'Every part is delivered')
  const moved: Record<PartId, number> = { hull_plating: 0, electronics: 0, drive_parts: 0 }
  let total = 0
  for (const part of PART_IDS) {
    const n = core.career.removeCargo(part, Math.min(core.career.cargo(part), missing[part]))
    if (n > 0) addDelivered(core.state, core.galaxy, cmd.relayId, part, n)
    moved[part] = n
    total += n
  }
  if (total === 0) return refuse('cargo', texts(ctx).noParts)
  const site = ctx.slots.sites[slot]
  emitReach(core.events, REV.partsDelivered, site.index, 0, ctx.slots.port[slot * 3], ctx.slots.port[slot * 3 + 1], ctx.slots.port[slot * 3 + 2], total)
  const done = missingTotal(core, ctx.slots, slot) === 0
  return { ok: true, message: `Delivered ${partsText(moved)}${done ? '. Every part is in: begin the reboot' : ''}` }
}

export function beginReboot(ctx: RelinkContext, core: ReachCore, sim: Sim, cmd: Cmd<'beginReboot'>): ReachCommandResult {
  if (core.relink.phase !== 'idle') return refuse('busy', texts(ctx).busy)
  const slot = slotFor(ctx, core, cmd.relayId)
  if (typeof slot !== 'number') return slot
  const state = notOffline(core, ctx, slot)
  if (state !== null) return state
  if (!isScanned(core, cmd.relayId)) return refuse('invalid', `Scan the relay first (fly within ${Math.round(ctx.cfg.autoScanM / 1000)} km)`)
  if (missingTotal(core, ctx.slots, slot) > 0) return refuse('invalid', `Deliver ${partsText(missingAt(core, ctx, slot))} first`)
  const p = atPort(ctx, core, sim, slot, false)
  if (typeof p !== 'number') return p
  if (core.travel.mode !== 'normal') return refuse('busy', 'Drop to normal flight first')
  startReboot(core, sim, ctx, slot)
  return { ok: true, message: `Reboot started: defend the relay for ${minutesSeconds(core.relink.duration)}` }
}

export function repairLaneRing(ctx: RelinkContext, core: ReachCore, sim: Sim, cmd: Cmd<'repairLaneRing'>): ReachCommandResult {
  const ring = core.session.layout?.laneRings.find((r) => r.id === cmd.ringId)
  if (ring === undefined) return refuse('invalid', 'That lane ring is not in this system')
  if (laneRingUp(core.state, ring)) return refuse('invalid', 'The lane ring is already up')
  const p = livePlayer(core, sim)
  const range = ctx.cfg.laneRepair.rangeM
  if (p < 0 || dist2(p, ring.position[0], ring.position[1], ring.position[2]) > range * range) {
    return refuse('outOfRange', `Fly within ${Math.round(range)} m of the lane ring`)
  }
  const need = ctx.cfg.laneRepair.parts
  for (const part of PART_IDS) if (core.career.cargo(part) < (need[part] ?? 0)) return refuse('cargo', texts(ctx).laneNeeds)
  for (const part of PART_IDS) {
    const n = need[part] ?? 0
    if (n > 0) core.career.removeCargo(part, n)
  }
  setLaneRingUp(core.state, ring, true)
  core.session.setStructureState('laneRing', ring.index, 1)
  emitReach(core.events, REV.laneRingRepaired, ring.index, 0, ring.position[0], ring.position[1], ring.position[2], 0)
  return { ok: true, message: 'Lane ring repaired' }
}

/** Factions that may sponsor: `factions.json` `sponsors`, and always the Authority. */
function maySponsor(ctx: RelinkContext, faction: string): faction is FactionKey {
  return faction === 'authority' || (ctx.factions.sponsors as readonly string[]).includes(faction)
}

export function chooseSponsor(ctx: RelinkContext, core: ReachCore, _sim: Sim, cmd: Cmd<'chooseSponsor'>): ReachCommandResult {
  const index = core.galaxy.relays.findIndex((r) => r.id === cmd.relayId)
  if (index < 0) return refuse('invalid', 'Unknown relay')
  const rs = core.state.relays[cmd.relayId]
  if (rs === undefined || !rs.sponsorPending) return refuse('invalid', 'That relay has no sponsor to choose')
  if (!maySponsor(ctx, cmd.faction)) return refuse('invalid', `${cmd.faction} cannot sponsor a relay`)
  rs.sponsor = cmd.faction
  rs.sponsorPending = false
  emitReach(core.events, REV.sponsorChosen, index, FACTION_KEYS.indexOf(cmd.faction), 0, 0, 0, 0)
  if (persistable(core)) core.persist('auto')
  const name = ctx.factions.factions[cmd.faction]?.name ?? cmd.faction
  return { ok: true, message: `${name} sponsors the relink` }
}
